//-- The dependency arrays below are deliberately narrow; widening them re-runs option loading
//-- and re-registers the save action on every render.
/* eslint-disable react-hooks/exhaustive-deps */

import { produce } from "immer";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { DateTimePicker } from "react-widgets";
import {
	Button,
	Confirm,
	Dropdown,
	Form,
	Grid,
	Icon,
	Input,
	Loader,
	Message,
	Modal,
	Popup,
	Segment,
} from "semantic-ui-react";
import type { SemanticWIDTHS } from "semantic-ui-react";

import "./style.css";

import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as trialActions from "../../../../redux/actions/trialActions";
import * as editTrialAuth from "./editTrialAuth";
import { EditTrialUserAuth } from "./editTrialAuth";
import { TrialValidation, validateTrial } from "./trialValidation";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import {
	UserClientAssociation,
	UserClientAssociations,
	WorkingTrial,
	WorkingTrialSponsor,
} from "../../../../apis/types/trialTypes";

import RemoveTrialOwnerSponsorModal from "./RemoveTrialOwnerSponsorModal";
import SelectUserClientModal from "./SelectUserClientModal";

/** Saves the trial and reports whether it passed validation. Registered with the wizard. */
type SaveTrial = () => Promise<boolean>;

interface ClientMembership {
	clientId: string;
	clientName: string;
}

/** The members of the `useUserAuth` value this component uses. */
interface EditTrialUserAuthValue extends EditTrialUserAuth {
	user: {
		userInfo: {
			basic: { userId: string; isAerialPlotAdmin?: boolean; isAerialPlotSupport?: boolean };
			clientMemberships: ClientMembership[];
		};
	};
	currentClient: ClientMembership | null;
}

interface SelectOption {
	key: string;
	value: string;
	text: string;
}

/** The {@link WorkingTrial} fields `etWorkingTrialSelect` can bind a dropdown to. */
type WorkingTrialSelectKey = "meanComparisonId" | "alphaId" | "flightScheduleId";

/** An entry of the `entityNames` slice's `relatedLinks`. */
interface RelatedLink {
	type: string;
}

interface TrialOptionsResponse {
	companies: { id: string; name: string }[];
	meanComparisons: { id: string; name: string }[];
	alphas: { id: string; value: string }[];
	flightScheduleOptions: { id: string; name: string }[];
	emailAddresses: { emailAddress: string }[];
}

interface UpdateTrialResponse {
	statusCode: number;
	messages?: { type: string; text: string }[];
}

/** The response to assigning a trial owner or sponsor by email address. */
interface AssignUserResponse {
	errors?: string[];
	userId: string;
	firstName: string;
	lastName: string;
	singleClientId?: string | null;
	singleClientName?: string | null;
	isPending: boolean;
	message: string;
}

/** An owner/sponsor assignment paused while the user picks which of the assignee's orgs to use. */
interface PendingAssignment {
	type: "Owner" | "Sponsor";
	emailAddress: string;
	userName: string;
	clients: UserClientAssociation[];
}

interface EditTrialProps {
	navigateToNextStep: () => void;
	initOriginalTrial: WorkingTrial | null;
	initWorkingTrial: WorkingTrial;
	initTrialOwnerEmailAddress: string;
	setSaveAction: (setter: () => SaveTrial) => void;
	setIsDirty: (isDirty: boolean) => void;
}

const EditTrial = ({
	navigateToNextStep,
	initOriginalTrial,
	initWorkingTrial,
	initTrialOwnerEmailAddress,
	setSaveAction,
	setIsDirty,
}: EditTrialProps) => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth() as EditTrialUserAuthValue;
	const moduleNavigation = useModuleNavigation();
	const entityNames = useSelector((state) => (state.entityNames ? state.entityNames : null));

	const relatedLinks = (entityNames?.relatedLinks ?? []) as RelatedLink[];
	const trialIsInProtocol = relatedLinks.some((rl) => rl.type === "protocol");

	const DATE_FORMAT = "M/D/YYYY";

	//-- Dropdown options
	const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
	const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
	const [meanComparisonOptions, setMeanComparisonOptions] = useState<SelectOption[]>([]);
	const [alphaOptions, setAlphaOptions] = useState<SelectOption[]>([]);
	const [flightScheduleOptions, setFlightScheduleOptions] = useState<SelectOption[]>([]);
	const [emailAddressOptions, setEmailAddressOptions] = useState<SelectOption[]>([]);

	//-- Trial data
	const [originalTrial, setOriginalTrial] = useState(initOriginalTrial);
	const [workingTrial, setWorkingTrialSilently] = useState(initWorkingTrial);
	const [trialOwnerEmailAddress, setTrialOwnerEmailAddress] = useState(initTrialOwnerEmailAddress);
	const [trialSponsorEmailAddress, setTrialSponsorEmailAddress] = useState("");

	//-- Validation
	const [trialIsValid, setTrialIsValid] = useState(true);
	const [trialValidation, setTrialValidation] = useState<TrialValidation>({
		isValid: true,
		messages: [],
		treatments: { messages: [] },
	});

	//-- UI Control
	const [loading, setLoading] = useState(true);
	const [modalLoading, setModalLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [emailModalType, setEmailModalType] = useState("");
	const [trialOwnerChangingTrialOwner, setTrialOwnerChangingTrialOwner] = useState(false);
	const [showSendEmailConfirm, setShowSendEmailConfirm] = useState(false);
	const [granteeCompanyId, setGranteeCompanyId] = useState<string | null>(null);
	const [isTrialDirty, setIsTrialDirty] = useState(false);

	//For Admin dropdown
	const [emailAddressForOwner, setEmailAddressForOwner] = useState<string | null>(null);
	const [emailAddressForSponsor, setEmailAddressForSponsor] = useState<string | null>(null);

	const [showSelectClientModal, setShowSelectClientModal] = useState(false);
	const [pendingAssignment, setPendingAssignment] = useState<PendingAssignment | null>(null);

	const [openRemoveTrialOwnerSponsorModal, setOpenRemoveTrialOwnerSponsorModal] = useState(false);
	const [removeTrialOwnerOrSponsor, setRemoveTrialOwnerOrSponsor] = useState("");

	const [activeSponsor, setActiveSponsor] = useState<WorkingTrialSponsor | null>(null);

	const currentUserId = userAuth.user.userInfo.basic.userId;
	const trialSponsors = workingTrial?.trialSponsors ?? [];
	const currentUserSponsorEntry = trialSponsors.find(
		(s) => userAuth.currentClientId === s.clientId || currentUserId === s.userId,
	);
	const isOwnerViewer =
		(userAuth.currentClientId === workingTrial.trialOwnerClientId || currentUserId === workingTrial.trialOwnerUserId) &&
		workingTrial.publishedToTrialOwner;
	const isSponsorViewer =
		workingTrial.publishedToTrialSponsor &&
		(!!currentUserSponsorEntry ||
			userAuth.currentClientId === workingTrial.trialSponsorClientId ||
			currentUserId === workingTrial.trialSponsorUserId);
	const isPublisher = userAuth.currentClientId === workingTrial.clientId;

	const save = useCallback<SaveTrial>(async () => {
		const validation = validateTrial(workingTrial);
		setTrialIsValid(validation.isValid);
		setTrialValidation(validation);

		if (!validation.isValid) {
			window.scrollTo(0, 0);
			return false;
		}

		const trialUpdateObj = getUpdateObject();

		setIsSaving(true);

		try {
			const accessToken = await getTokenSilently();
			const res = (await trialActions.updateTrial(trialUpdateObj, accessToken)(dispatch)) as UpdateTrialResponse;

			if (res.statusCode === 200) {
				toast.success("Trial saved successfully");
				setOriginalTrial(originalTrial ? { ...originalTrial, name: trialUpdateObj.trialName } : originalTrial);
			} else if (res.statusCode === 400) {
				(res.messages ?? []).forEach((m) => {
					if (m.type === "Informational") {
						toast.info(m.text);
					} else if (m.type === "FailedValidation") {
						toast.error(m.text);
					}
				});
			}
		} catch (error) {
			const errors = (error as { errors?: string[] } | null)?.errors;
			if (errors) {
				errors.forEach((e) => toast.error(e));
			} else {
				toast.error("Trial failed to update");
			}
		} finally {
			setIsSaving(false);
			setIsTrialDirty(false);
		}

		return true;
	}, [workingTrial, userAuth.currentClientId]);

	useEffect(() => {
		setIsDirty(isTrialDirty);
	}, [isTrialDirty]);

	useEffect(() => {
		if (userAuth.currentClientId && moduleNavigation.trialId) {
			void getTrialOptions(userAuth.currentClientId);
		}
	}, [userAuth.currentClientId]);

	useEffect(() => {
		setSaveAction(() => save);
	}, [save]);

	const setWorkingTrial = (newState: WorkingTrial) => {
		setIsTrialDirty(true);
		setWorkingTrialSilently(newState);
	};

	async function getTrialOptions(clientId: string) {
		const accessToken = await getTokenSilently();
		setLoading(true);

		try {
			const res = (await trialActions.getTrialOptions(
				clientId,
				moduleNavigation.trialId,
				accessToken,
			)(dispatch)) as TrialOptionsResponse;

			initializeCompanyOptions(res.companies);
			initializeClientOptions(userAuth.user.userInfo.clientMemberships);
			initializeMeanComparisons(res.meanComparisons);
			initializeAlphas(res.alphas);
			initializeFlightScheduleOptions(res.flightScheduleOptions);
			initializeEmailAddressOptions(res.emailAddresses);
		} catch (err) {
			console.log("Error loading trial options");
			console.log(err);
		} finally {
			setLoading(false);
		}
	}

	const initializeCompanyOptions = (companies: TrialOptionsResponse["companies"]) => {
		const companyOpts = _.map(companies, (c) => {
			const opt = {
				key: c.id,
				value: c.id,
				text: c.name,
			};
			return opt;
		});
		const filteredCompanyOpts = _.filter(companyOpts, (c) => {
			return !!c.key;
		});
		setCompanyOptions(filteredCompanyOpts);
	};

	const initializeClientOptions = (clients: ClientMembership[]) => {
		const clientOpts = _.map(clients, (c) => {
			const opt = {
				key: c.clientId,
				value: c.clientId,
				text: c.clientName,
			};
			return opt;
		});
		setClientOptions(clientOpts);
	};

	function initializeMeanComparisons(meanComparisons: TrialOptionsResponse["meanComparisons"]) {
		const meanComparisonOpts = _.map(meanComparisons, (mc) => {
			const opt = {
				key: mc.id,
				value: mc.id,
				text: mc.name,
			};
			return opt;
		});
		setMeanComparisonOptions(meanComparisonOpts);
	}

	function initializeAlphas(alphas: TrialOptionsResponse["alphas"]) {
		const sortedAlphas = _.sortBy(alphas, "value");
		const alphaOpts = _.map(sortedAlphas, (a) => {
			const opt = {
				key: a.id,
				value: a.id,
				text: a.value,
			};
			return opt;
		});
		setAlphaOptions(alphaOpts);
	}

	const initializeFlightScheduleOptions = (flightScheduleData: TrialOptionsResponse["flightScheduleOptions"]) => {
		const flightScheduleOpts = _.map(flightScheduleData, (fs) => {
			const opt = {
				key: fs.id,
				value: fs.id,
				text: fs.name,
			};
			return opt;
		});
		let filteredFlightScheduleOpts = _.filter(flightScheduleOpts, (c) => {
			return !!c.key;
		});
		filteredFlightScheduleOpts = _.orderBy(filteredFlightScheduleOpts, ["text"]);
		setFlightScheduleOptions(filteredFlightScheduleOpts);
	};

	const initializeEmailAddressOptions = (emailAddressData: TrialOptionsResponse["emailAddresses"]) => {
		const emailAddressOpts = _.map(emailAddressData, (ea) => {
			const opt = {
				key: ea.emailAddress,
				value: ea.emailAddress,
				text: ea.emailAddress,
			};
			return opt;
		});
		let filteredEmailAddressOpts = _.filter(emailAddressOpts, (c) => {
			return !!c.key;
		});
		filteredEmailAddressOpts = _.orderBy(filteredEmailAddressOpts, (filteredEmailAddressOpts) =>
			filteredEmailAddressOpts.text.toLowerCase(),
		);
		setEmailAddressOptions(filteredEmailAddressOpts);
	};

	const getUpdateObject = () => {
		const trialUpdateObj = {
			clientId: userAuth.currentClientId,
			trialId: moduleNavigation.trialId,
			trialName: editTrialAuth.userCanEditTrialName(userAuth) ? workingTrial.name : (originalTrial?.name ?? ""),
			trialOwnerCompanyId: workingTrial.trialOwnerCompanyId,
			trialOwnerClientId: workingTrial.trialOwnerClientId,
			plantDate: editTrialAuth.userCanEditPlantDate(userAuth, workingTrial.clientId)
				? workingTrial.plantDate
				: (originalTrial?.plantDate ?? null),
			harvestDate: editTrialAuth.userCanEditHarvestDate(userAuth, workingTrial.clientId)
				? workingTrial.harvestDate
				: (originalTrial?.harvestDate ?? null),
			treatments: editTrialAuth.userCanEditTreatments(userAuth)
				? workingTrial.treatments
				: (originalTrial?.treatments ?? []),
			meanComparisonId: workingTrial.meanComparisonId,
			alphaId: workingTrial.alphaId,
			isEstimatedPlantDate: workingTrial.isEstimatedPlantDate,
			flightScheduleId: workingTrial.flightScheduleId,
		};

		return trialUpdateObj;
	};

	const canLookUpClientAssociations =
		userAuth.isApAdmin ||
		userAuth.user.userInfo.basic.isAerialPlotAdmin === true ||
		userAuth.user.userInfo.basic.isAerialPlotSupport === true;

	/**
	 * Looks up the organizations `emailAddress` belongs to. Returns null when the lookup is
	 * unavailable -- the caller lacks permission, no user has that address, or the call failed -- in
	 * which case assignment proceeds with the client id it would have used anyway.
	 */
	const lookUpClientAssociations = async (emailAddress: string): Promise<UserClientAssociations | null> => {
		if (!canLookUpClientAssociations) return null;

		try {
			const accessToken = await getTokenSilently();
			const res = (await trialActions.getClientAssociationsForEmailAddress(
				emailAddress,
				userAuth.currentClientId,
				accessToken,
			)(dispatch)) as UserClientAssociations;

			//-- An unknown email address is reported by the assignment call itself, which also offers to
			//-- invite the user, so nothing is lost by letting it through here.
			return res.errors ? null : res;
		} catch {
			return null;
		}
	};

	/** Assigns the trial owner. `selectedClient` is the organization the user picked, if any. */
	const assignTrialOwner = async (emailAddress: string, selectedClient: UserClientAssociation | null) => {
		const accessToken = await getTokenSilently();
		const res = (await trialActions.setTrialOwnerEmailAddress(
			emailAddress,
			workingTrial.id,
			userAuth.currentClientId,
			selectedClient?.clientId ?? workingTrial.trialOwnerClientId,
			trialOwnerChangingTrialOwner,
			workingTrial.publishedToTrialOwner,
			workingTrial.fieldResearcherId,
			trialSponsors[0]?.userId ?? null,
			accessToken,
		)(dispatch)) as AssignUserResponse;

		if (res.errors && res.errors.length > 0) {
			toast.error(res.errors[0]);

			if (res.errors[0].includes("A user does not exist")) {
				setShowSendEmailConfirm(true);
			}
		} else {
			const newWorkingTrial = produce(workingTrial, (draft) => {
				draft.trialOwnerUserId = res.userId;
				draft.trialOwner = `${res.firstName} ${res.lastName}`;
				draft.trialOwnerClientId = res.singleClientId ?? selectedClient?.clientId ?? null;
				draft.trialOwnerClientName = res.singleClientName ?? selectedClient?.clientName ?? null;
				draft.trialOwnerIsPending = res.isPending;
			});
			setWorkingTrialSilently(newWorkingTrial);

			toast.success(res.message);
			setShowEmailModal(false);
		}
	};

	/** Adds a trial sponsor. `selectedClient` is the organization the user picked, if any. */
	const assignTrialSponsor = async (emailAddress: string, selectedClient: UserClientAssociation | null) => {
		const accessToken = await getTokenSilently();
		const res = (await trialActions.addTrialSponsorEmailAddress(
			emailAddress,
			workingTrial.id,
			userAuth.currentClientId,
			granteeCompanyId,
			selectedClient?.clientId ?? null,
			workingTrial.fieldResearcherId,
			workingTrial.trialOwnerUserId,
			accessToken,
		)(dispatch)) as AssignUserResponse;

		if (res.errors) {
			toast.error(res.errors);

			if (res.errors[0].includes("A user does not exist")) {
				setShowSendEmailConfirm(true);
			}
		} else {
			const companyName = companyOptions.find((c) => c.value === granteeCompanyId)?.text ?? null;
			const addedSponsor: WorkingTrialSponsor = {
				userId: res.userId,
				name: `${res.firstName} ${res.lastName}`,
				firstName: res.firstName,
				lastName: res.lastName,
				clientId: res.singleClientId ?? selectedClient?.clientId ?? null,
				clientName: res.singleClientName ?? selectedClient?.clientName ?? null,
				companyId: granteeCompanyId,
				companyName: companyName,
				isPending: res.isPending,
				emailAddress: emailAddress,
			};
			const newWorkingTrial = produce(workingTrial, (draft) => {
				if (!draft.trialSponsors) draft.trialSponsors = [];
				const idx = draft.trialSponsors.findIndex((s) => s.userId === addedSponsor.userId);
				if (idx >= 0) {
					draft.trialSponsors[idx] = addedSponsor;
				} else {
					draft.trialSponsors.push(addedSponsor);
				}
			});
			setWorkingTrialSilently(newWorkingTrial);

			toast.success(res.message);
			setShowEmailModal(false);
			setGranteeCompanyId(null);
		}
	};

	/**
	 * Runs the organization lookup ahead of an assignment. Returns the organization to assign to, or
	 * false when the user has been asked to pick one and the assignment should wait for their answer.
	 */
	const resolveAssignmentClient = async (
		type: PendingAssignment["type"],
		emailAddress: string,
	): Promise<UserClientAssociation | null | false> => {
		const associations = await lookUpClientAssociations(emailAddress);
		if (!associations) return null;

		if (associations.hasMultipleClients) {
			setPendingAssignment({
				type,
				emailAddress,
				userName: `${associations.firstName} ${associations.lastName}`.trim(),
				clients: associations.clients,
			});
			setShowSelectClientModal(true);
			return false;
		}

		//-- No organizations at all is a valid answer; assign without one, as before the lookup existed.
		return associations.clients[0] ?? null;
	};

	const handleSaveTrialOwnerEmailAddress = async () => {
		const emailAddress = (userAuth.isApAdmin ? emailAddressForOwner : trialOwnerEmailAddress) ?? "";

		if (_.isEmpty(emailAddress)) {
			toast.error("Please enter a valid email address");
			return;
		}

		setModalLoading(true);

		try {
			const selectedClient = await resolveAssignmentClient("Owner", emailAddress);
			if (selectedClient !== false) {
				await assignTrialOwner(emailAddress, selectedClient);
			}
		} finally {
			setModalLoading(false);
		}
	};

	const handleSaveTrialSponsorEmailAddress = async () => {
		const emailAddress = (userAuth.isApAdmin ? emailAddressForSponsor : trialSponsorEmailAddress) ?? "";

		if (_.isEmpty(emailAddress)) {
			toast.error("Please enter a valid email address");
			return;
		}

		setModalLoading(true);

		try {
			const selectedClient = await resolveAssignmentClient("Sponsor", emailAddress);
			if (selectedClient !== false) {
				await assignTrialSponsor(emailAddress, selectedClient);
			}
		} finally {
			setModalLoading(false);
		}
	};

	/** Completes the assignment the organization picker was opened for. */
	const handleSelectUserClient = async (client: UserClientAssociation) => {
		if (!pendingAssignment) return;

		setModalLoading(true);

		try {
			if (pendingAssignment.type === "Owner") {
				await assignTrialOwner(pendingAssignment.emailAddress, client);
			} else {
				await assignTrialSponsor(pendingAssignment.emailAddress, client);
			}
		} finally {
			setModalLoading(false);
			setShowSelectClientModal(false);
			setPendingAssignment(null);
		}
	};

	const sendNewUserEmail = async (emailAddress: string) => {
		// Same splitting logic as below
		let clientName = workingTrial.fieldResearcher;
		if (isOwnerViewer) {
			clientName = workingTrial.trialOwnerClientName
				? workingTrial.trialOwnerClientName
				: (userAuth.currentClient?.clientName ?? "");
		} else if (isSponsorViewer) {
			clientName = currentUserSponsorEntry?.clientName
				? currentUserSponsorEntry.clientName
				: (userAuth.currentClient?.clientName ?? "");
		}

		try {
			const accessToken = await getTokenSilently();
			await trialActions.sendNewUserEmail(emailAddress, clientName, userAuth.currentClientId, accessToken)(dispatch);

			toast.success("Email was sent to that address");
			setShowSendEmailConfirm(false);
			setShowEmailModal(false);
		} catch (error) {
			const errors = (error as { errors?: string[] } | null)?.errors;
			if (errors) {
				errors.forEach((e) => toast.error(e));
			}
			setShowSendEmailConfirm(false);
		}
	};

	function setPlantDate(date: Date | null | undefined) {
		if (date) {
			const plantDate = new Date(date);
			plantDate.setHours(0, 0, 0, 0);
			const newWorkingTrial = produce(workingTrial, (draft) => {
				draft.plantDate = plantDate;
			});
			setWorkingTrial(newWorkingTrial);
		} else {
			const newWorkingTrial = produce(workingTrial, (draft) => {
				draft.plantDate = null;
			});
			setWorkingTrial(newWorkingTrial);
		}
	}

	function setHarvestDate(date: Date | null | undefined) {
		if (date) {
			const harvestDate = new Date(date);
			harvestDate.setHours(0, 0, 0, 0);
			const newWorkingTrial = produce(workingTrial, (draft) => {
				draft.harvestDate = harvestDate;
			});
			setWorkingTrial(newWorkingTrial);
		} else {
			const newWorkingTrial = produce(workingTrial, (draft) => {
				draft.harvestDate = null;
			});
			setWorkingTrial(newWorkingTrial);
		}
	}

	const openAssignSponsorModal = () => {
		setActiveSponsor(null);
		setEmailModalType("Sponsor");
		setTrialSponsorEmailAddress("");
		setEmailAddressForSponsor("");
		setShowEmailModal(true);
	};

	const openRemoveSponsorModal = (sponsor: WorkingTrialSponsor | null) => {
		setActiveSponsor(sponsor ?? null);
		setRemoveTrialOwnerOrSponsor("Sponsor");
		setOpenRemoveTrialOwnerSponsorModal(true);
	};

	const renderSponsorList = (canManage: boolean) => {
		const canAssignSponsor =
			canManage && userAuth.hasApplicationArea(ApplicationArea.TrialAssignSponsor, userAuth.currentClientId);
		return (
			<Form.Field width="6">
				<label>Trial Sponsors</label>
				{trialSponsors.length !== 0 && (
					<>
						<div style={{ display: "flex", flexDirection: "row", gap: "5px", marginBottom: "3px" }}>
							<span style={{ flexBasis: "19%", fontWeight: 700 }}>Sponsor Company</span>
							<span style={{ flexGrow: 1, fontWeight: 700 }}>Trial Sponsor</span>
							<span style={{ flexBasis: "30%", fontWeight: 700 }}>Trial Sponsor Org</span>
							<span style={{ width: "72px" }} />
						</div>
						{trialSponsors.map((sponsor) => (
							<div
								key={sponsor.userId ?? sponsor.emailAddress}
								style={{ display: "flex", flexDirection: "row", gap: "5px", marginBottom: "5px" }}
							>
								<Input style={{ flexBasis: "50%" }} value={sponsor.companyName ?? ""} readOnly />
								<Input
									style={{ flexGrow: 1 }}
									value={`${sponsor.name}${sponsor.isPending ? " (Pending)" : ""}`}
									readOnly
								/>
								<div
									style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flexBasis: "75%" }}
								>
									<Input value={sponsor.clientName ? sponsor.clientName : "Not Assigned"} readOnly />
									{sponsor.isPending && (
										<Popup
											trigger={<Icon name="info circle" />}
											content="This user does not have the correct license to view this trial."
										/>
									)}
								</div>
								{!isSponsorViewer && (
									<Button
										style={{ margin: 0 }}
										negative
										compact
										content="Remove"
										onClick={() => openRemoveSponsorModal(sponsor)}
										disabled={!canAssignSponsor}
									/>
								)}
							</div>
						))}
					</>
				)}
				<div style={{ display: "flex", flexDirection: "row", gap: "5px", marginTop: "5px" }}>
					<Dropdown
						selection
						search
						style={{ flexGrow: 1 }}
						id="form-select-grantee-company"
						options={companyOptions}
						value={granteeCompanyId ?? ""}
						onChange={(event, { value }) => setGranteeCompanyId(value as string)}
						loading={loading && (!companyOptions || companyOptions.length === 0)}
						placeholder="Sponsor Company"
						disabled={!canAssignSponsor}
					/>
					<Button
						style={{ margin: 0 }}
						primary
						content="Assign Trial Sponsor"
						onClick={() => openAssignSponsorModal()}
						disabled={!canAssignSponsor}
					/>
				</div>
			</Form.Field>
		);
	};

	const etLabel = (text: string, isPending: boolean | undefined, htmlFor: string) => (
		<label htmlFor={htmlFor}>
			{text}{" "}
			{isPending && (
				<Popup
					trigger={<Icon name="info circle" />}
					content="This user does not have the correct license to view this trial."
				/>
			)}
		</label>
	);

	const etReadonly = (label: ReactNode, value: string | number, width: SemanticWIDTHS, id: string) => (
		<Form.Input label={label} width={width} id={id} value={value ?? ""} readOnly />
	);

	const etPlantDate = (width: SemanticWIDTHS) => (
		<Form.Field width={width} error={trialValidation.plantDateError}>
			<label htmlFor="form-input-plant-date">Plant Date</label>
			{editTrialAuth.userCanEditPlantDate(userAuth, workingTrial.clientId) ? (
				<DateTimePicker includeTime={false} value={workingTrial.plantDate} onChange={(date) => setPlantDate(date)} />
			) : (
				<Input
					id="form-input-plant-date"
					fluid
					value={workingTrial.plantDate ? moment(workingTrial.plantDate).local().format(DATE_FORMAT) : ""}
					readOnly
				/>
			)}
		</Form.Field>
	);

	const etHarvestDate = (width: SemanticWIDTHS) => (
		<Form.Field width={width} error={trialValidation.harvestDateError}>
			<label htmlFor="form-input-harvest-date">Harvest Date</label>
			{editTrialAuth.userCanEditHarvestDate(userAuth, workingTrial.clientId) ? (
				<DateTimePicker
					includeTime={false}
					value={workingTrial.harvestDate}
					onChange={(date) => setHarvestDate(date)}
				/>
			) : (
				<Input
					id="form-input-harvest-date"
					fluid
					value={workingTrial.harvestDate ? moment(workingTrial.harvestDate).local().format(DATE_FORMAT) : ""}
					readOnly
				/>
			)}
		</Form.Field>
	);

	const etIsEstimatedCheckbox = () => (
		<Form.Checkbox
			label="Is Estimated Plant Date"
			style={{ marginTop: "2.5em" }}
			id="form-is-estimate-plant-date"
			readOnly={!editTrialAuth.userCanEditPlantDate(userAuth, workingTrial.clientId)}
			checked={workingTrial.isEstimatedPlantDate}
			onChange={(e, { checked }) => {
				const newWorkingTrial = produce(workingTrial, (draft) => {
					draft.isEstimatedPlantDate = checked ?? false;
				});
				setWorkingTrial(newWorkingTrial);
			}}
		/>
	);

	const etWorkingTrialSelect = (
		label: string,
		width: SemanticWIDTHS,
		id: string,
		valueKey: WorkingTrialSelectKey,
		options: SelectOption[],
		error?: boolean,
	) => (
		<Form.Select
			label={label}
			width={width}
			id={id}
			search
			style={{ minWidth: "unset" }}
			options={options}
			value={workingTrial[valueKey]}
			onChange={(event, { value }) => {
				const newWorkingTrial = produce(workingTrial, (draft) => {
					draft[valueKey] = value as string;
				});
				setWorkingTrial(newWorkingTrial);
			}}
			loading={!options || options.length === 0}
			error={!!error}
		/>
	);

	const etMeanAlphaFlightRow = () => (
		<Form.Group>
			{etWorkingTrialSelect(
				"Mean Comparison",
				"3",
				"form-select-mean-comparison",
				"meanComparisonId",
				meanComparisonOptions,
			)}
			{etWorkingTrialSelect("Alpha", "3", "form-select-alpha", "alphaId", alphaOptions)}
			{etWorkingTrialSelect(
				"Flight Schedule",
				"6",
				"form-select-flight-schedule",
				"flightScheduleId",
				flightScheduleOptions,
				trialValidation.flightScheduleId,
			)}
		</Form.Group>
	);

	const etOrgField = () => {
		if (isOwnerViewer) {
			const orgReadOnly = currentUserId !== workingTrial.trialOwnerUserId && !userAuth.isApAdmin;
			return orgReadOnly ? (
				<Form.Input label="Trial Owner Org" width="6" value={workingTrial.trialOwnerClientName ?? ""} readOnly />
			) : (
				<Form.Select
					label="Trial Owner Org"
					width="6"
					id="form-select-client"
					search
					options={clientOptions}
					value={workingTrial.trialOwnerClientId ?? ""}
					onChange={(event, { value }) => {
						const newWorkingTrial = produce(workingTrial, (draft) => {
							draft.trialOwnerClientId = value as string;
						});
						setWorkingTrial(newWorkingTrial);
					}}
					loading={!clientOptions || clientOptions.length === 0}
				/>
			);
		}
		if (isSponsorViewer) {
			return (
				<Form.Input
					label="Trial Sponsor Org"
					width="6"
					id="form-input-trial-sponsor-org"
					value={currentUserSponsorEntry?.clientName ?? ""}
					readOnly
				/>
			);
		}
		if (isPublisher) {
			return (
				<Form.Select
					label="Company"
					width="6"
					id="form-select-company"
					search
					options={companyOptions}
					value={workingTrial.trialOwnerCompanyId ?? ""}
					onChange={(event, { value }) => {
						const newWorkingTrial = produce(workingTrial, (draft) => {
							draft.trialOwnerCompanyId = value as string;
						});
						setWorkingTrial(newWorkingTrial);
					}}
					loading={loading && (!companyOptions || companyOptions.length === 0)}
					error={!!trialValidation.companyError}
				/>
			);
		}
		return null;
	};

	const etPersonField = () => {
		if (isOwnerViewer) {
			return (
				<Form.Field width="6">
					{etLabel("Trial Owner", workingTrial.trialOwnerIsPending, "form-select-trial-owner")}
					<div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
						<Input
							style={{ flexGrow: 1 }}
							value={`${workingTrial.trialOwner} ${workingTrial.trialOwnerIsPending ? "(Pending)" : ""}`}
							readOnly
						/>
						<Button
							style={{ margin: 0 }}
							primary
							compact
							content="Change"
							onClick={() => {
								setTrialOwnerChangingTrialOwner(true);
								setEmailModalType("Owner");
								setShowEmailModal(true);
							}}
						/>
					</div>
				</Form.Field>
			);
		}
		if (isSponsorViewer) {
			return (
				<Form.Field width="6">
					{etLabel("Trial Sponsor", currentUserSponsorEntry?.isPending, "form-select-trial-owner")}
					<div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
						<Input
							fluid
							style={{ flexGrow: 1 }}
							value={`${currentUserSponsorEntry?.name ?? ""} ${currentUserSponsorEntry?.isPending ? "(Pending)" : ""}`}
							readOnly
						/>
					</div>
				</Form.Field>
			);
		}
		if (isPublisher) {
			return (
				<Form.Field width="6">
					{etLabel("Trial Owner", workingTrial.trialOwnerIsPending, "form-select-trial-owner")}
					{workingTrial.trialOwnerUserId ? (
						<div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
							<Input
								style={{ flexGrow: 1 }}
								fluid
								value={`${workingTrial.trialOwner} ${workingTrial.trialOwnerIsPending ? " (Pending)" : ""}`}
								readOnly
							/>
							<Button
								style={{ margin: 0 }}
								primary
								compact
								content="Change"
								onClick={() => {
									if (trialIsInProtocol) {
										toast.error("Cannot change trial owner when trial is assigned to a protocol");
									} else {
										setTrialOwnerChangingTrialOwner(false);
										setEmailModalType("Owner");
										setShowEmailModal(true);
									}
								}}
							/>
							<Button
								style={{ margin: 0 }}
								negative
								compact
								content="Remove"
								onClick={() => {
									if (trialIsInProtocol) {
										toast.error("Cannot remove trial owner when trial is assigned to a protocol");
									} else if (trialSponsors.length > 0) {
										toast.error("Cannot remove trial owner when trial sponsor is set");
									} else {
										setOpenRemoveTrialOwnerSponsorModal(true);
										setRemoveTrialOwnerOrSponsor("Owner");
									}
								}}
							/>
						</div>
					) : (
						<Button
							primary
							fluid
							content="Assign Trial Owner"
							onClick={() => {
								setTrialOwnerChangingTrialOwner(false);
								setEmailModalType("Owner");
								setShowEmailModal(true);
							}}
						/>
					)}
				</Form.Field>
			);
		}
		return null;
	};

	return loading ? (
		<Loader active />
	) : !loading && !originalTrial ? (
		<Segment style={{ margin: 15 }}>
			<p>{"We could not find this trial."}</p>
		</Segment>
	) : (
		<>
			<div>
				<div
					style={{ display: "flex", alignItems: "end", textWrap: "nowrap", overflow: "hidden", marginBottom: "1em" }}
				>
					<div style={{ flexGrow: 1, minWidth: 10 }} />
					<Button
						compact
						id="form-button-save"
						primary
						content="Save"
						loading={isSaving}
						disabled={isSaving}
						onClick={() => void save()}
					/>
					<Button
						compact
						id="form-button-save"
						positive
						content="Save and Continue"
						loading={isSaving}
						disabled={isSaving}
						onClick={() => {
							void save().then(() => navigateToNextStep());
						}}
					/>
				</div>
			</div>
			<div>
				{/* HEADER */}

				{/* FORM */}
				<Form error={!trialIsValid} style={{ gap: ".5em", display: "flex", flexDirection: "column" }}>
					{!trialIsValid && (
						<Form.Group>
							<Form.Field width="16" style={{ marginBottom: 10 }}>
								<Message error list={trialValidation.messages} />
							</Form.Field>
						</Form.Group>
					)}
					<Form.Group>
						<Form.Input
							label="Trial Name"
							width="6"
							id="form-input-trial-name"
							defaultValue={workingTrial.name}
							onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
								const newWorkingTrial = produce(workingTrial, (draft) => {
									draft.name = event.target.value;
								});
								setWorkingTrial(newWorkingTrial);
							}}
							error={trialValidation.nameError}
							readOnly={!editTrialAuth.userCanEditTrialName(userAuth)}
						/>
						{etReadonly("Field Researcher", workingTrial.fieldResearcher ?? "", "6", "form-input-field-researcher")}
					</Form.Group>
					<Form.Group>
						{etOrgField()}
						{etReadonly("Location", workingTrial.location ?? "", "6", "form-input-location")}
					</Form.Group>
					<Form.Group>
						{etPersonField()}
						{etReadonly(
							"GPS Coordinates (Plot 101)",
							workingTrial.gpsCoordinates ?? "",
							"6",
							"form-input-gps-coordinates",
						)}
					</Form.Group>
					{isOwnerViewer ? (
						<>
							<Form.Group>
								{renderSponsorList(true)}
								{etPlantDate("6")}
								{etIsEstimatedCheckbox()}
							</Form.Group>
							<Form.Group>
								{etReadonly("Treatment Count", workingTrial.treatments?.length ?? "", "3", "form-input-treatments")}
								{etReadonly("Rep Count", workingTrial.repCount ?? "", "3", "form-input-reps")}
								{etHarvestDate("6")}
							</Form.Group>
							{etMeanAlphaFlightRow()}
						</>
					) : isSponsorViewer ? (
						<>
							<Form.Group>
								{etReadonly(
									"Trial Owner Org",
									workingTrial.trialOwnerClientName ? workingTrial.trialOwnerClientName : "Not Assigned",
									"6",
									"form-trial-owner-org",
								)}
								{etReadonly("Treatment Count", workingTrial.treatments?.length ?? "", "3", "form-input-treatments")}
								{etReadonly("Rep Count", workingTrial.repCount ?? "", "3", "form-input-reps")}
							</Form.Group>
							<Form.Group>
								{etReadonly(
									etLabel("Trial Owner", workingTrial.trialOwnerIsPending, "form-trial-owner"),
									`${workingTrial.trialOwner} ${workingTrial.trialOwnerIsPending ? "(Pending)" : ""}`,
									"6",
									"form-trial-owner",
								)}
								{etPlantDate("6")}
								{etIsEstimatedCheckbox()}
							</Form.Group>
							<Form.Group>
								{etReadonly(
									"Trial Sponsor Org",
									currentUserSponsorEntry?.clientName ? currentUserSponsorEntry.clientName : "Not Assigned",
									"6",
									"input-trial-sponsor-org",
								)}
								{etHarvestDate("6")}
							</Form.Group>
							{etMeanAlphaFlightRow()}
						</>
					) : isPublisher ? (
						<>
							<Form.Group>
								{etReadonly(
									"Trial Owner Org",
									workingTrial.trialOwnerClientName ? workingTrial.trialOwnerClientName : "Not Assigned",
									"6",
									"input-trial-owner-org",
								)}
								{etPlantDate("6")}
								{etIsEstimatedCheckbox()}
							</Form.Group>
							<Form.Group>
								{etReadonly("Treatment Count", workingTrial.treatments?.length ?? "", "3", "form-input-treatments")}
								{etReadonly("Rep Count", workingTrial.repCount ?? "", "3", "form-input-reps")}
								{etHarvestDate("6")}
							</Form.Group>
							{etMeanAlphaFlightRow()}
						</>
					) : null}

					<Confirm
						open={showSendEmailConfirm}
						header="A user with that email does not exist in the system"
						content={`Do you want to send an email to '${
							emailModalType === "Owner"
								? trialOwnerEmailAddress
								: userAuth.isApAdmin
									? emailAddressForSponsor
									: trialSponsorEmailAddress
						}' informing them of this trial?`}
						onCancel={() => setShowSendEmailConfirm(false)}
						onConfirm={() => {
							void sendNewUserEmail(
								(emailModalType === "Owner"
									? trialOwnerEmailAddress
									: userAuth.isApAdmin
										? emailAddressForSponsor
										: trialSponsorEmailAddress) ?? "",
							);
						}}
					/>
					<Modal open={showEmailModal}>
						<Modal.Header>Assign Trial {emailModalType}</Modal.Header>
						<Modal.Content>
							<Modal.Description>
								<Grid>
									{userAuth.isApAdmin ? (
										<Grid.Row>
											<Grid.Column width={12}>
												<label>
													<b>Enter Email</b>
												</label>
												<Form.Dropdown
													style={{ width: "100%" }}
													id="form-select-email"
													search
													fluid
													options={emailAddressOptions}
													value={(emailModalType === "Owner" ? emailAddressForOwner : emailAddressForSponsor) ?? ""}
													onChange={(event, { value }) => {
														if (emailModalType === "Owner") {
															setEmailAddressForOwner(value as string);
														} else {
															setEmailAddressForSponsor(value as string);
														}
													}}
													onSearchChange={(event, { searchQuery }) => {
														if (emailModalType === "Owner") {
															setEmailAddressForOwner(searchQuery);
														} else {
															setEmailAddressForSponsor(searchQuery);
														}
													}}
													loading={!emailAddressOptions || emailAddressOptions.length === 0}
													placeholder="Email Address"
													selection
													type="text"
													selectOnBlur={false}
													openOnFocus
													searchQuery={
														(emailModalType === "Owner" ? emailAddressForOwner : emailAddressForSponsor) ?? ""
													}
												/>
											</Grid.Column>
											<Grid.Column width={2}>
												<br></br>
												<Button
													onClick={() => {
														if (emailModalType === "Owner") {
															setEmailAddressForOwner("");
														} else {
															setEmailAddressForSponsor("");
														}
													}}
												>
													Clear
												</Button>
											</Grid.Column>
										</Grid.Row>
									) : (
										<Grid.Row>
											<Grid.Column width={12}>
												<label>
													<b>Enter Email</b>
												</label>
												<Input
													// key={resetKey}
													fluid
													id="input-email"
													value={emailModalType === "Owner" ? trialOwnerEmailAddress : trialSponsorEmailAddress}
													onChange={(event) => {
														if (emailModalType === "Owner") {
															setTrialOwnerEmailAddress(event.target.value);
														} else {
															setTrialSponsorEmailAddress(event.target.value);
														}
													}}
													readOnly={modalLoading}
													placeholder="Email Address"
												/>
											</Grid.Column>
											<Grid.Column width={2}>
												<br></br>
												<Button
													onClick={() => {
														if (emailModalType === "Owner") {
															setTrialOwnerEmailAddress("");
														} else {
															setTrialSponsorEmailAddress("");
														}
													}}
												>
													Clear
												</Button>
											</Grid.Column>
										</Grid.Row>
									)}
								</Grid>
							</Modal.Description>
						</Modal.Content>
						<Modal.Actions>
							<Button
								type="button"
								floated="right"
								onClick={() => {
									if (emailModalType === "Owner") {
										void handleSaveTrialOwnerEmailAddress();
									} else {
										void handleSaveTrialSponsorEmailAddress();
									}
								}}
								positive
								loading={modalLoading}
								disabled={modalLoading}
							>
								Save
							</Button>
							<Button type="button" floated="right" onClick={() => setShowEmailModal(false)} disabled={modalLoading}>
								Cancel
							</Button>
							<br style={{ clear: "both" }} />
						</Modal.Actions>
					</Modal>
				</Form>
			</div>
			<SelectUserClientModal
				key={pendingAssignment ? `${pendingAssignment.type}-${pendingAssignment.emailAddress}` : "none"}
				open={showSelectClientModal}
				assignmentType={pendingAssignment?.type ?? ""}
				emailAddress={pendingAssignment?.emailAddress ?? ""}
				userName={pendingAssignment?.userName ?? ""}
				clients={pendingAssignment?.clients ?? []}
				saving={modalLoading}
				onCancel={() => {
					setShowSelectClientModal(false);
					setPendingAssignment(null);
				}}
				onConfirm={(client) => {
					void handleSelectUserClient(client);
				}}
			/>
			<RemoveTrialOwnerSponsorModal
				open={openRemoveTrialOwnerSponsorModal}
				setOpen={(isOpen) => {
					setOpenRemoveTrialOwnerSponsorModal(isOpen);
					if (!isOpen) setActiveSponsor(null);
				}}
				trialId={workingTrial.id}
				clientId={userAuth.currentClientId ?? ""}
				removeTrialOwnerOrSponsor={removeTrialOwnerOrSponsor}
				sponsorUserId={activeSponsor?.userId ?? null}
				sponsorClientId={activeSponsor?.clientId ?? null}
				setWorkingTrialSilently={setWorkingTrialSilently}
				workingTrial={workingTrial}
			/>
		</>
	);
};

export default EditTrial;
